/* eslint-disable @typescript-eslint/no-explicit-any */
import { FabricObject, FabricText, FabricImage } from 'fabric';

import { MarkerOptions, MarkerLayoutConfig, TextEntityType } from '../../types/fabric-extensions';
import { generateObjectId } from '../../utils/generateId';

/**
 * Класс Marker - селектируемая коробка-контейнер для любого FabricObject
 * Позиционируется согласно textEntityStyles и содержит переданный с бэкенда объект
 */
export class Marker extends FabricObject {
  id: string;
  entityId: string; // ID связанной текстовой сущности
  entityType: TextEntityType; // Тип сущности для определения стилей
  isMarker: boolean; // Флаг что это маркер

  // Связанные объекты из конфигурации (переданные с бэкенда)
  // Теперь маркер может содержать несколько объектов вместо группы
  private contentObjects: FabricObject[] = [];

  static type = 'marker';

  constructor(options: MarkerOptions) {
    // Создаем селектируемый контейнер-коробку
    super({
      left: options.left || 0,
      top: options.top || 0,
      width: options.width || 16,
      height: options.height || 16,
      originX: (options.originX as any) || 'center', // Сохраняем originX из бэкенда (по умолчанию center для маркеров)
      originY: (options.originY as any) || 'center', // Сохраняем originY из бэкенда (по умолчанию center для маркеров)
      fill: 'transparent', // Сама коробка прозрачная
      stroke: '#007bff', // Синяя рамка для визуализации при выделении
      strokeWidth: 0, // По умолчанию невидимая
      strokeDashArray: [3, 3], // Пунктирная линия
      // Настройки взаимодействия
      selectable: true, // ✅ Маркеры выделяемы
      evented: true,
      hasControls: false, // Без контролов изменения размера
      hasBorders: true, // ✅ С рамкой при выделении
      lockMovementX: true, // 🔒 Нельзя двигать (связан с TextEntity)
      lockMovementY: true, // 🔒 Нельзя двигать (связан с TextEntity)
      lockRotation: true, // 🔒 Нельзя поворачивать
      lockScalingX: true, // 🔒 Нельзя масштабировать
      lockScalingY: true, // 🔒 Нельзя масштабировать
      opacity: 1,
      visible: true,
    });

    this.id = options.id || generateObjectId('marker');
    this.entityId = options.entityId;
    this.entityType = options.entityType;
    this.isMarker = true;

    // Подписываемся на события
    this.on('selected', () => {
      this.set('strokeWidth', 1); // Показываем рамку при выделении
      this.canvas?.requestRenderAll();
    });

    this.on('deselected', () => {
      this.set('strokeWidth', 0); // Скрываем рамку при снятии выделения
      this.canvas?.requestRenderAll();
    });

    // Если маркер двигается, синхронизируем контентный объект
    this.on('moving', () => {
      this.syncContentObjectPosition();
    });

    this.on('modified', () => {
      this.syncContentObjectPosition();
    });
  }

  /**
   * Добавляет связанный объект из конфигурации
   * @param fabricObject - FabricObject переданный с бэкенда
   */
  addContentObject(fabricObject: FabricObject): void {
    // 🚨 КРИТИЧЕСКАЯ ПРОВЕРКА: TextEntity не может быть contentObject!
    if (fabricObject.type === 'textEntity' || (fabricObject as any).isMetaBulletEntity) {
      console.error('🚨 CRITICAL ERROR: Attempted to set TextEntity as marker content!', {
        markerId: this.id,
        entityId: this.entityId,
        attemptedContentId: fabricObject.id,
        attemptedContentType: fabricObject.type,
        isMetaBulletEntity: (fabricObject as any).isMetaBulletEntity,
      });
      throw new Error(
        `Cannot set TextEntity as marker content! Marker: ${this.id}, TextEntity: ${fabricObject.id}`,
      );
    }

    // Проверяем что объект еще не добавлен
    const alreadyAdded = this.contentObjects.some((obj) => obj.id === fabricObject.id);
    if (alreadyAdded) {
      console.warn('⚠️ Content object already added to this marker:', fabricObject.id);
      return;
    }

    // Добавляем в массив
    this.contentObjects.push(fabricObject);

    // Проверяем, нужно ли добавлять контент на канвас
    // (он может уже быть добавлен в loadObjectsWithLayers)
    if (this.canvas) {
      const isContentOnCanvas = this.canvas.getObjects().includes(fabricObject);

      if (!isContentOnCanvas) {
        this.canvas.add(fabricObject);
      }
    }

    // ВАЖНО: Корректируем и блокируем ПОСЛЕ добавления на канвас
    this.adjustContentObjectToContainer(fabricObject);
    this.forceDisableContentInteraction(fabricObject);

    if (this.canvas) {
      this.canvas.requestRenderAll();

      // Многократная проверка что объект заблокирован
      this.enforceContentLock(fabricObject);
    }
  }

  /**
   * Устанавливает связанный объект из конфигурации (legacy метод для обратной совместимости)
   * @deprecated Используйте addContentObject вместо этого
   */
  setContentObject(fabricObject: FabricObject): void {
    // Очищаем старые объекты если были
    this.clearContentObjects();
    // Добавляем новый
    this.addContentObject(fabricObject);
  }

  /**
   * Удаляет конкретный объект контента из маркера
   * @param fabricObject - Объект для удаления
   * @returns true если объект был найден и удален, false в противном случае
   */
  removeContentObject(fabricObject: FabricObject): boolean {
    const index = this.contentObjects.indexOf(fabricObject);

    if (index === -1) {
      return false;
    }

    // Удаляем из массива
    this.contentObjects.splice(index, 1);

    return true;
  }

  /**
   * Очищает все связанные объекты контента
   */
  clearContentObjects(): void {
    if (this.canvas) {
      this.contentObjects.forEach((obj) => {
        if (this.canvas!.getObjects().includes(obj)) {
          this.canvas!.remove(obj);
        }
      });
    }

    this.contentObjects = [];
  }

  /**
   * Синхронизирует позиции всех контентных объектов с позицией маркера-контейнера
   * ВАЖНО: Учитывает разные системы координат marker и content
   */
  private syncContentObjectPosition(): void {
    if (this.contentObjects.length === 0) return;

    // Вызываем синхронную версию с конвертацией координат
    this.syncContentObjectPositionSync();

    // Принудительно рендерим изменения
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  }

  /**
   * 🚀 ОПТИМИЗАЦИЯ: Синхронная версия без requestRenderAll
   * ВАЖНО: Учитывает разные системы координат marker и content
   */
  public syncContentObjectPositionSync(): void {
    if (this.contentObjects.length === 0) return;

    // Получаем текущую позицию и размеры маркера
    const markerLeft = this.left || 0;
    const markerTop = this.top || 0;
    const markerWidth = this.width || 16;
    const markerHeight = this.height || 16;

    // Определяем систему координат маркера
    // ВАЖНО: По умолчанию Fabric.js использует 'center', НЕ 'left'!
    const markerOriginX = this.originX || 'center';
    const markerOriginY = this.originY || 'center';

    // Вычисляем ЦЕНТР маркера (независимо от его origin)
    let markerCenterX = markerLeft;
    let markerCenterY = markerTop;

    if (markerOriginX === 'left') {
      markerCenterX = markerLeft + markerWidth / 2;
    } else if (markerOriginX === 'right') {
      markerCenterX = markerLeft - markerWidth / 2;
    }

    if (markerOriginY === 'top') {
      markerCenterY = markerTop + markerHeight / 2;
    } else if (markerOriginY === 'bottom') {
      markerCenterY = markerTop - markerHeight / 2;
    }

    this.contentObjects.forEach((contentObject) => {
      // Пропускаем если контент в ActiveSelection
      if (contentObject.group?.type.toLowerCase() === 'activeselection') {
        return;
      }

      // Получаем сохраненные оффсеты (в центральной системе координат)
      // ВАЖНО: offsetY всегда 0 - контент всегда на одной вертикали с центром маркера
      const offsetX = contentObject._markerOffsetX ?? 0;
      const offsetY = 0;

      // Вычисляем новый центр контента
      const newContentCenterX = markerCenterX + offsetX;
      const newContentCenterY = markerCenterY + offsetY;

      // Конвертируем обратно в систему координат контента
      // КРИТИЧЕСКИ ВАЖНО: Используем СОХРАНЕННУЮ систему координат из _markerOriginX/_markerOriginY
      // а не текущую originX/originY, т.к. она могла измениться!
      const contentOriginX = contentObject._markerOriginX || contentObject.originX || 'center';
      const contentOriginY = contentObject._markerOriginY || contentObject.originY || 'center';
      const contentWidth = (contentObject.width || 0) * (contentObject.scaleX || 1);
      const contentHeight = (contentObject.height || 0) * (contentObject.scaleY || 1);

      let newLeft = newContentCenterX;
      let newTop = newContentCenterY;

      // Конвертируем из центра в систему координат контента
      if (contentOriginX === 'left') {
        newLeft = newContentCenterX - contentWidth / 2;
      } else if (contentOriginX === 'right') {
        newLeft = newContentCenterX + contentWidth / 2;
      }

      if (contentOriginY === 'top') {
        newTop = newContentCenterY - contentHeight / 2;
      } else if (contentOriginY === 'bottom') {
        newTop = newContentCenterY + contentHeight / 2;
      }

      // Обновляем позицию
      contentObject.set({
        left: newLeft,
        top: newTop,
      });

      contentObject.setCoords();
    });

    // НЕ вызываем requestRenderAll() для синхронности
  }

  /**
   * Корректирует позицию и размеры контентного объекта согласно контейнеру маркера
   * Также сохраняет оффсеты для последующей синхронизации
   *
   * ВАЖНО: Учитывает разные системы координат (marker может быть left/top, content - center/center)
   */
  private adjustContentObjectToContainer(contentObject: FabricObject): void {
    // Если оффсет X уже есть (например, скопирован с placeholder), пропускаем вычисление
    // ВАЖНО: offsetY всегда игнорируем, используем только offsetX
    const hasExistingOffsetX = contentObject._markerOffsetX !== undefined;

    if (hasExistingOffsetX) {
      // Принудительно устанавливаем offsetY = 0 при наличии существующего offsetX
      contentObject._markerOffsetY = 0;
      return;
    }

    // Получаем позицию маркера в его системе координат
    const markerLeft = this.left || 0;
    const markerWidth = this.width || 16;

    // Вычисляем ЦЕНТР маркера по X (независимо от его origin)
    // ВАЖНО: По умолчанию Fabric.js использует 'center', НЕ 'left'!
    const markerOriginX = this.originX || 'center';

    let markerCenterX = markerLeft;

    // Конвертируем позицию маркера в центр по X
    if (markerOriginX === 'left') {
      markerCenterX = markerLeft + markerWidth / 2;
    } else if (markerOriginX === 'right') {
      markerCenterX = markerLeft - markerWidth / 2;
    }
    // Если уже center - оставляем как есть

    // Получаем позицию контента в его системе координат
    const contentLeft = contentObject.left || 0;
    const contentWidth = (contentObject.width || 0) * (contentObject.scaleX || 1);

    // Получаем систему координат контента и конвертируем к строковому типу
    // ВАЖНО: По умолчанию Fabric.js использует 'center', НЕ 'left'!
    const contentOriginX =
      typeof contentObject.originX === 'string' ? contentObject.originX : 'center';
    const contentOriginY =
      typeof contentObject.originY === 'string' ? contentObject.originY : 'center';

    let contentCenterX = contentLeft;

    // Конвертируем позицию контента в центр по X
    if (contentOriginX === 'left') {
      contentCenterX = contentLeft + contentWidth / 2;
    } else if (contentOriginX === 'right') {
      contentCenterX = contentLeft - contentWidth / 2;
    }

    // Вычисляем оффсет X в ЦЕНТРАЛЬНОЙ системе координат
    const offsetX = contentCenterX - markerCenterX;
    // ВАЖНО: offsetY всегда 0 - контент всегда на одной вертикали с центром маркера
    const offsetY = 0;

    // Сохраняем оффсеты и информацию о системе координат КОНТЕНТА
    // КРИТИЧЕСКИ ВАЖНО: Сохраняем originX/originY КОНТЕНТА, а не маркера!
    // Это нужно для правильной конвертации обратно при синхронизации
    contentObject._markerOffsetX = offsetX;
    contentObject._markerOffsetY = offsetY;
    contentObject._markerOriginX = contentOriginX;
    contentObject._markerOriginY = contentOriginY;
  }

  /**
   * 🚀 ОПТИМИЗАЦИЯ: Синхронная версия - просто синхронизирует контент с маркером
   */
  public adjustContentObjectToContainerSync(): void {
    this.syncContentObjectPositionSync();
  }

  /**
   * Обновляет позицию маркера и его контента согласно textEntityStyles
   * @param entityLeft - left координата родительской сущности
   * @param entityTop - top координата родительской сущности
   * @param entityHeight - высота родительской текстовой коробки
   * @param markerConfig - конфигурация маркера из textEntityStyles
   */
  updatePosition(
    entityLeft: number,
    entityTop: number,
    entityHeight: number,
    markerConfig: MarkerLayoutConfig,
  ): void {
    const offsetX = markerConfig.position.offsetX || 0;
    const newWidth = markerConfig.position.width;
    const newHeight = markerConfig.position.height;

    // Рассчитываем координату ЛЕВОГО КРАЯ маркера
    const markerLeftEdge = entityLeft - newWidth - offsetX;

    // Корректируем в зависимости от origin маркера
    const newLeft =
      this.originX === 'center'
        ? markerLeftEdge + newWidth / 2 // Для center origin добавляем половину ширины
        : markerLeftEdge; // Для left origin оставляем как есть

    // Рассчитываем newTop в зависимости от типа offsetY
    let markerTopEdge: number;
    const offsetY = markerConfig.position.offsetY;

    if (typeof offsetY === 'number') {
      // Числовое значение - используем как есть
      markerTopEdge = entityTop + offsetY;
    } else if (offsetY === 'top') {
      // Верхняя граница маркера = верхняя граница текста
      markerTopEdge = entityTop;
    } else if (offsetY === 'center') {
      // Верхний край маркера для центрирования по вертикали
      markerTopEdge = entityTop + (entityHeight - newHeight) / 2;
    } else if (offsetY === 'bottom') {
      // Верхняя граница маркера = нижняя граница текста минус высота маркера
      markerTopEdge = entityTop + entityHeight - newHeight;
    } else {
      // Fallback
      markerTopEdge = entityTop;
    }

    // Корректируем в зависимости от originY маркера
    const newTop =
      this.originY === 'center'
        ? markerTopEdge + newHeight / 2 // Для center origin добавляем половину высоты
        : markerTopEdge; // Для top origin оставляем как есть

    // Обновляем позицию и размеры контейнера
    const needsUpdate =
      this.left !== newLeft ||
      this.top !== newTop ||
      this.width !== newWidth ||
      this.height !== newHeight;

    if (needsUpdate) {
      this.set({
        left: newLeft,
        top: newTop,
        width: newWidth,
        height: newHeight,
      });
      this.setCoords();

      // 🚀 ОПТИМИЗАЦИЯ: Синхронное обновление контента без задержек
      this.adjustContentObjectToContainerSync();

      // Дополнительно синхронизируем позицию
      this.syncContentObjectPositionSync();
    }
  }

  /**
   * Обновляет стили маркера (применяется ко всем объектам контента)
   * @param markerConfig - новая конфигурация маркера
   */
  updateStyle(markerConfig: MarkerLayoutConfig): void {
    if (this.contentObjects.length === 0 || !markerConfig.style) return;

    const newFill = markerConfig.style.fill || '#000000';
    const newOpacity = markerConfig.style.opacity || 1;
    const newStroke = markerConfig.style.stroke;
    const newStrokeWidth = markerConfig.style.strokeWidth || 0;

    this.contentObjects.forEach((contentObject) => {
      // Применяем стили к контенту (для текста и SVG)
      if (contentObject instanceof FabricText) {
        contentObject.set({
          fill: newFill,
          opacity: newOpacity,
          stroke: newStroke,
          strokeWidth: newStrokeWidth,
        });
      }

      // Для изображений применяем только opacity
      if (contentObject instanceof FabricImage) {
        contentObject.set('opacity', newOpacity);
      }
    });

    this.canvas?.requestRenderAll();
  }

  /**
   * Скрыть маркер и все его объекты контента
   */
  hide(): void {
    this.set('visible', false);
    this.contentObjects.forEach((contentObject) => {
      contentObject.set('visible', false);
    });
  }

  /**
   * Показать маркер и все его объекты контента
   */
  show(): void {
    this.set('visible', true);
    this.contentObjects.forEach((contentObject) => {
      contentObject.set('visible', true);
    });
  }

  /**
   * Переопределяем метод для корректного добавления на канвас
   */
  onCanvas(): void {
    // Добавляем все объекты контента на канвас после добавления самого маркера
    if (this.canvas && this.contentObjects.length > 0) {
      this.contentObjects.forEach((contentObject) => {
        if (!this.canvas!.getObjects().includes(contentObject)) {
          this.canvas!.add(contentObject);
        }
      });
      this.canvas.requestRenderAll();
    }
  }

  /**
   * Переопределяем метод для корректного удаления с канваса
   */
  onRemoved(): void {
    // Удаляем все объекты контента с канваса
    if (this.canvas && this.contentObjects.length > 0) {
      this.contentObjects.forEach((contentObject) => {
        this.canvas!.remove(contentObject);
      });

      this.canvas.requestRenderAll();
    }
  }

  /**
   * Безопасное удаление маркера с очисткой всех связей
   */
  safeDelete(): void {
    // Удаляем контент
    this.onRemoved();

    // Удаляем себя с канваса (canvas.remove будет вызван извне)
  }

  /**
   * РАДИКАЛЬНО отключает интерактивность объекта контента
   */
  private forceDisableContentInteraction(fabricObject: FabricObject): void {
    // Базовые свойства связи
    fabricObject.set({
      markerId: this.id,
      entityId: this.entityId,
      isMarkerContent: true,
    });

    // КРИТИЧЕСКИЕ свойства интерактивности
    const lockProperties = {
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      lockSkewingX: true,
      lockSkewingY: true,
      lockUniScaling: true,
      lockScalingFlip: true,
      moveCursor: 'default',
      hoverCursor: 'default',
      perPixelTargetFind: false,
      excludeFromExport: false,
    };

    // Устанавливаем все свойства одновременно
    fabricObject.set(lockProperties);

    // ДОПОЛНИТЕЛЬНАЯ блокировка через setControlsVisibility
    if (
      'setControlsVisibility' in fabricObject &&
      typeof fabricObject.setControlsVisibility === 'function'
    ) {
      fabricObject.setControlsVisibility({
        mt: false, // top-middle
        mb: false, // bottom-middle
        ml: false, // left-middle
        mr: false, // right-middle
        bl: false, // bottom-left
        br: false, // bottom-right
        tl: false, // top-left
        tr: false, // top-right
        mtr: false, // rotation
      });
    }
  }

  /**
   * Принудительно обеспечивает блокировку контента с множественными проверками
   */
  private enforceContentLock(contentObject: FabricObject): void {
    // Немедленная проверка
    this.verifyAndFixContentLock(contentObject);

    // Проверки с задержками для преодоления асинхронных переопределений
    setTimeout(() => this.verifyAndFixContentLock(contentObject), 10);
    setTimeout(() => this.verifyAndFixContentLock(contentObject), 50);
    setTimeout(() => this.verifyAndFixContentLock(contentObject), 100);
    setTimeout(() => this.verifyAndFixContentLock(contentObject), 200);
  }

  /**
   * Проверяет и исправляет состояние блокировки контента
   */
  private verifyAndFixContentLock(contentObject: FabricObject): void {
    const shouldFix =
      contentObject.selectable === true ||
      contentObject.evented === true ||
      contentObject.hasControls === true ||
      contentObject.hasBorders === true ||
      contentObject.lockMovementX !== true ||
      contentObject.lockMovementY !== true;

    if (shouldFix) {
      console.warn('⚠️ Content object lock compromised, re-enforcing:', {
        contentObjectId: contentObject.id,
        selectable: contentObject.selectable,
        evented: contentObject.evented,
        hasControls: contentObject.hasControls,
        hasBorders: contentObject.hasBorders,
      });

      // Повторно блокируем
      this.forceDisableContentInteraction(contentObject);

      // Принудительно рендерим после исправления
      if (this.canvas) {
        this.canvas.requestRenderAll();
      }
    }
  }

  /**
   * Получить первый связанный контентный объект (legacy метод для обратной совместимости)
   * @deprecated Используйте getContentObjects() для получения всех объектов
   */
  getContentObject(): FabricObject | undefined {
    return this.contentObjects[0];
  }

  /**
   * Получить все связанные контентные объекты
   */
  getContentObjects(): FabricObject[] {
    return [...this.contentObjects];
  }

  /**
   * Проверить есть ли связанные контентные объекты
   */
  hasContentObject(): boolean {
    return this.contentObjects.length > 0;
  }

  /**
   * Получить количество связанных контентных объектов
   */
  getContentObjectsCount(): number {
    return this.contentObjects.length;
  }

  // Статические свойства для автоматической сериализации кастомных полей
  static customProperties = ['entityId', 'entityType', 'isMarker'];
}
